import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { EmptyState, FlexBox, Text } from '@dixa/lib-shared-habla';
import { colors, sizes, typography } from '@dixa/lib-shared-tokens';
import { Fragment, memo, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Bar, BarChart, CartesianGrid, Label, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { useInstrumentComparison } from '../../comparison/instrumentComparison.context';
import styles from './chartInstrument.module.scss';
import { ChartInstrumentGranularity } from './components/granularity/chartInstrumentGranularity';
import { ChartInstrumentTooltip } from './components/tooltip/chartInstrumentTooltip';
import { ChartInstrumentVisualization } from './components/visualization/chartInstrumentVisualization';
import { useChartInstrumentAxes } from './components/wrappers/useChartInstrumentAxes/useChartInstrumentAxes';
import { useChartInstrumentLegend } from './components/wrappers/useChartInstrumentLegend/useChartInstrumentLegend';
import { useChartInstrumentSeries } from './components/wrappers/useChartInstrumentSeries/useChartInstrumentSeries';
import { visualizationIcons } from './utils/appearance';
import { transformDataForRecharts } from './utils/dataUtils';
export const ChartInstrument = memo(function ChartInstrument({ instrument, panel, onChangeGranularity, }) {
    var _a;
    const comparison = useInstrumentComparison();
    const [visualization, setVisualization] = useState(instrument.visualization);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const { data, layout, title } = instrument;
    const isHorizontal = layout === 'horizontal';
    const isVertical = layout === 'vertical';
    const rechartsData = useMemo(() => transformDataForRecharts(data, { omitZero: visualization === 'bar' }), [data, visualization]);
    const granularity = useMemo(() => { var _a, _b; return (_b = (_a = data.granularities) === null || _a === void 0 ? void 0 : _a.find(({ selected }) => selected)) === null || _b === void 0 ? void 0 : _b.value; }, [data.granularities]);
    const { xAxisProps, yAxisProps } = useChartInstrumentAxes({
        instrument,
        granularity,
    });
    const { legend, legendProps, hoveredLegend } = useChartInstrumentLegend({
        instrument,
        visualization,
    });
    const { getSeriesProps } = useChartInstrumentSeries({
        instrument,
        legend,
        hoveredLegend,
        hoveredCategory: hoveredCategory,
    });
    useUpdateEffect(() => {
        setVisualization(instrument.visualization);
    }, [instrument.visualization]);
    /**
     * Handles setting the hovered category when the mouse moves over the chart.
     *
     * The hovered category is only updated when there is at least one data point
     * for it in the chart. This is to prevent unnecessary re-renders when the
     * mouse moves over categories with no data to display in the tooltip.
     */
    const handleMouseMove = (state) => {
        var _a;
        if (!(state === null || state === void 0 ? void 0 : state.isTooltipActive)) {
            setHoveredCategory(null);
            return;
        }
        const index = state.activeTooltipIndex;
        if (typeof index !== 'number')
            return;
        const hasData = Object.values((_a = rechartsData[index]) !== null && _a !== void 0 ? _a : {}).length > 1;
        setHoveredCategory(hasData ? index : null);
    };
    const handleMouseLeave = () => {
        setHoveredCategory(null);
    };
    const renderChartSeries = () => {
        if (visualization === 'line') {
            const lines = getSeriesProps(visualization);
            return lines.map((lineProps, index) => (_jsx(Line, Object.assign({}, lineProps), index)));
        }
        if (visualization === 'bar') {
            const bars = getSeriesProps(visualization);
            return bars.map((barProps, index) => _jsx(Bar, Object.assign({}, barProps), index));
        }
        return null;
    };
    const renderChart = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const legendLayout = (_b = (_a = data.options) === null || _a === void 0 ? void 0 : _a.legend) === null || _b === void 0 ? void 0 : _b.layout;
        const chartMargin = parseInt(sizes.spacer[300]);
        const defaultChartProps = {
            layout,
            syncId: ((_d = (_c = comparison === null || comparison === void 0 ? void 0 : comparison.syncOptions) === null || _c === void 0 ? void 0 : _c.chart) === null || _d === void 0 ? void 0 : _d.tooltip) ? panel.id : undefined,
            data: rechartsData,
            margin: {
                top: isHorizontal ? chartMargin : 0,
                right: isVertical || legendLayout === 'vertical' ? chartMargin : 0,
                bottom: legendLayout === 'horizontal' ? chartMargin / 2 : 0,
                left: 0,
            },
        };
        const chartChildren = (_jsxs(_Fragment, { children: [_jsx(XAxis, Object.assign({}, xAxisProps)), _jsx(YAxis, Object.assign({}, yAxisProps)), ((_e = data.options) === null || _e === void 0 ? void 0 : _e.cartesian) && (_jsx(CartesianGrid, { vertical: isVertical, horizontal: isHorizontal, stroke: colors.neutral['200'] })), (_f = data.references) === null || _f === void 0 ? void 0 : _f.map(({ value, label }, index) => (_jsx(ReferenceLine, Object.assign({}, (isHorizontal ? { y: value } : { x: value }), { stroke: colors.neutral['500'], strokeDasharray: "5 5", isFront: true }, { children: _jsx(Label, { value: label, offset: 8, fontSize: typography.size['200'], position: isHorizontal ? 'insideBottomLeft' : 'insideTopLeft' }) }), index))), ((_g = data.options) === null || _g === void 0 ? void 0 : _g.legend) && _jsx(Legend, Object.assign({}, legendProps)), ((_h = data.options) === null || _h === void 0 ? void 0 : _h.tooltip) && (_jsx(Tooltip, { content: (tooltipProps) => (_jsx(ChartInstrumentTooltip, { instrument: instrument, granularity: granularity, tooltipProps: tooltipProps })), cursor: { fill: 'transparent' } })), renderChartSeries()] }));
        switch (visualization) {
            case 'bar':
                return (_jsx(BarChart, Object.assign({}, defaultChartProps, { barGap: "1", onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave }, { children: chartChildren })));
            case 'line':
                return _jsx(LineChart, Object.assign({}, defaultChartProps, { children: chartChildren }));
        }
    };
    if (!data.categories.length || !data.series.length) {
        return (_jsx(EmptyState, { title: "No data available", description: "There is no data available for the selected period and filters.", icon: visualizationIcons[visualization] }));
    }
    const actions = [
        data.granularities && data.granularities.length > 1 ? (_jsx(ChartInstrumentGranularity, { panel: panel, granularities: data.granularities, onChangeGranularity: onChangeGranularity })) : null,
        ((_a = data.options) === null || _a === void 0 ? void 0 : _a.visualizations) ? (_jsx(ChartInstrumentVisualization, { panel: panel, visualization: visualization, onChangeVisualization: setVisualization })) : null,
    ];
    const showActions = actions.some(Boolean);
    return (_jsxs(_Fragment, { children: [title && (_jsx("div", { children: _jsx(Text.Heading, Object.assign({ variant: "heading-4" }, { children: title })) })), showActions && (_jsx(FlexBox, Object.assign({ alignSelf: "flex-end", gap: "300" }, { children: actions.map((action, index) => (_jsx(Fragment, { children: action }, index))) }))), _jsx(ResponsiveContainer, Object.assign({ className: styles.container, width: "100%", height: "100%", debounce: 100 }, { children: renderChart() }))] }));
});
